import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsApi } from "@/api/ux";
import { Button, Card } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { cn } from "@/design-system/lib/utils";
import type { NotificationDto } from "@/types/api";
import { useDateFormatter } from "@/utils/datetime";

/**
 * Full-page notification feed. Routed from
 * the bell popover's "View all" link. Filters by read/unread; clicking
 * an entry both routes to its <c>linkUrl</c> and marks it read.
 */
const PAGE_SIZE = 25;

export function NotificationsFeedPage() {
  const queryClient = useQueryClient();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: ["notifications", "feed", { unreadOnly, page, pageSize: PAGE_SIZE }],
    queryFn: () => notificationsApi.list(unreadOnly, page, PAGE_SIZE),
  });

  const markAll = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications", "feed"] }),
  });

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications", "feed"] }),
  });

  const rows = list.data?.data ?? [];
  const unreadCount = list.data?.unreadCount ?? 0;

  return (
    <>
      <PageHeader
        eyebrow="Activity"
        title="Notifications"
        description="Recent activity routed to you across modules. Click an entry to jump to the source."
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setUnreadOnly((v) => !v);
                setPage(1);
              }}
            >
              {unreadOnly ? "Show all" : "Unread only"}
            </Button>
            <Button
              variant="primary"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending || unreadCount === 0}
            >
              {markAll.isPending ? "Marking…" : `Mark all read (${unreadCount})`}
            </Button>
          </>
        }
      />
      {/* Self-capped: every row carries a running-prose `body` line, which at
          a 1536px content box would set to roughly 170 characters against a
          comfortable measure of 60-75. `max-w-5xl` follows `LeadDetailPage`.
          See `AppShell.tsx`. */}
      <Card className="flex flex-col p-0 max-w-5xl">
        {list.isLoading && (
          <div className="px-space-4 py-space-5 text-label uppercase tracking-label text-gray-mid">
            Loading…
          </div>
        )}
        {!list.isLoading && rows.length === 0 && (
          <div className="px-space-4 py-space-5 text-small text-gray-mid">
            {unreadOnly ? "No unread notifications." : "You have no notifications yet."}
          </div>
        )}
        {rows.map((n) => (
          <Row
            key={n.id}
            n={n}
            onMarkRead={() => markRead.mutate(n.id)}
          />
        ))}
        {rows.length > 0 && (
          <div className="flex items-center justify-between px-space-4 py-space-3 border-t border-border-light">
            <Button
              variant="secondary"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <div className="text-label uppercase tracking-label text-gray-mid">
              Page {page}
            </div>
            <Button
              variant="secondary"
              disabled={rows.length < PAGE_SIZE}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </Card>
    </>
  );
}

function Row({ n, onMarkRead }: { n: NotificationDto; onMarkRead: () => void }) {
  const { dateTime } = useDateFormatter();
  const handleClick = () => {
    if (!n.isRead) onMarkRead();
  };
  const inner = (
    <div className="flex items-start gap-space-3">
      <span
        aria-hidden
        className={cn(
          "mt-[6px] h-2 w-2 shrink-0 rounded-full",
          n.category === "Urgent" ? "bg-status-error" : "bg-gold",
          n.isRead && "opacity-30",
        )}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-space-2">
          <div className={cn("text-small", n.isRead ? "text-gray-mid" : "font-semibold text-onyx")}>
            {n.title}
          </div>
          {n.eventType && (
            <span className="text-label uppercase tracking-label text-gold-shadow">
              {n.eventType}
            </span>
          )}
        </div>
        {n.body && <p className="text-small text-gray-mid m-0 mt-[2px]">{n.body}</p>}
        <div className="text-label text-gray-mid mt-[2px]">
          {dateTime(n.createdAt)}
        </div>
      </div>
    </div>
  );

  if (n.linkUrl) {
    return (
      <Link
        to={n.linkUrl}
        onClick={handleClick}
        className={cn(
          "block px-space-4 py-space-3 border-b border-border-light last:border-b-0",
          "hover:bg-white-warm focus:outline-none focus-visible:bg-white-warm",
          !n.isRead && "bg-gold/[0.04]",
        )}
      >
        {inner}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={onMarkRead}
      className={cn(
        "block w-full text-left px-space-4 py-space-3 border-b border-border-light last:border-b-0",
        "hover:bg-white-warm focus:outline-none focus-visible:bg-white-warm",
        !n.isRead && "bg-gold/[0.04]",
      )}
    >
      {inner}
    </button>
  );
}
